import React from 'react';
import WikiIcon from '../shared/WikiIcon';
import { ASK_COPY } from './askCopy';

/**
 * "How this answer was made" (D-ask §1.3(6)) — Wiki admins only: the server
 * sends `trace` / `brief` / `rewrite` to admins and omits them for everyone
 * else, and the toggle only renders when `trace` is present.
 *
 * Stages are Heart / Brain / Driver / Writer cards with question → answer
 * items, a confidence (`"94%"`, `"0.91–0.97"`, or `"code"` → "Code check")
 * and a "Learned" badge; then "What JEV told the writer" (the brief) and the
 * before/after rewrite (answers only).
 */

const ROLE_CLASS = {
    Heart: 'wk-ask-role--heart',
    Brain: 'wk-ask-role--brain',
    Driver: 'wk-ask-role--driver',
    Writer: 'wk-ask-role--writer',
};

export function RoleChip({ role }) {
    return <span className={`wk-ask-role ${ROLE_CLASS[role] || 'wk-ask-role--writer'}`}>{role}</span>;
}

/** Contract items are `{q, a, p, learned}`; the prototype's canned data used `[q, a, p, learned]`. */
function toItem(item) {
    if (Array.isArray(item)) return { q: item[0], a: item[1], p: item[2], learned: Boolean(item[3]) };
    return {
        q: item?.q ?? '',
        a: item?.a ?? '',
        p: item?.p ?? '',
        learned: Boolean(item?.learned),
    };
}

export function confidenceText(p) {
    if (p === null || p === undefined || p === '') return '';
    if (p === 'code') return ASK_COPY.codeCheck;
    return String(p);
}

function TraceStage({ stage }) {
    const items = Array.isArray(stage?.items) ? stage.items.map(toItem) : [];
    return (
        <div className="wk-ask-stage" data-testid="wiki-ask-stage" data-role={stage?.role}>
            <div className="wk-ask-stage-head">
                <RoleChip role={stage?.role || 'Writer'} />
                <span className="wk-ask-stage-name">{stage?.stage}</span>
            </div>
            {items.map((item, index) => {
                const p = confidenceText(item.p);
                return (
                    // Items have no id; their order is the pipeline's order.
                    <div key={`${item.q}-${index}`} className="wk-ask-item">
                        <div className="wk-ask-item-q">
                            <span className="wk-ask-item-qtext">{item.q}</span>
                            {item.learned && (
                                <span className="wk-ask-learned">
                                    <WikiIcon name="sparkle" size={9} />
                                    {ASK_COPY.learned}
                                </span>
                            )}
                        </div>
                        <div className="wk-ask-item-a">
                            <span className="wk-ask-item-atext">{item.a}</span>
                            {p && <span className="wk-ask-item-p">{p}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function AskTrace({ trace, brief, rewrite, showRewrite = true }) {
    const stages = Array.isArray(trace?.stages) ? trace.stages : [];
    const briefText = typeof brief === 'string' ? brief : brief?.text;
    const hasRewrite = showRewrite && rewrite && (rewrite.from || rewrite.to);
    return (
        <div className="wk-ask-trace" data-testid="wiki-ask-trace">
            <div className="wk-ask-trace-head">
                <span className="wk-ask-trace-title">{trace?.title || ASK_COPY.traceTitle}</span>
                {trace?.subtitle && <span className="wk-ask-trace-sub">{trace.subtitle}</span>}
                <span className="wk-spacer" />
                {trace?.cost && <span className="wk-ask-trace-cost">{trace.cost}</span>}
            </div>
            {stages.length > 0 && (
                <div className="wk-ask-stages">
                    {stages.map((stage, index) => (
                        <TraceStage key={`${stage?.role}-${stage?.stage}-${index}`} stage={stage} />
                    ))}
                </div>
            )}
            {briefText && (
                <div className="wk-ask-brief" data-testid="wiki-ask-brief">
                    <div className="wk-ask-brief-head">
                        <RoleChip role="Driver" />
                        <span className="wk-ask-brief-label">{ASK_COPY.briefLabel}</span>
                    </div>
                    <div className="wk-ask-brief-text">{briefText}</div>
                </div>
            )}
            {hasRewrite && (
                <div className="wk-ask-rewrite" data-testid="wiki-ask-rewrite">
                    <div className="wk-ask-brief-head">
                        <RoleChip role="Writer" />
                        <span className="wk-ask-rewrite-label">{ASK_COPY.rewriteLabel}</span>
                    </div>
                    <div className="wk-ask-rewrite-grid">
                        <div>
                            <div className="wk-ask-rewrite-side">{ASK_COPY.onThePage}</div>
                            <div className="wk-ask-rewrite-from">{rewrite.from}</div>
                        </div>
                        <WikiIcon name="arrow-right" size={14} className="wk-ask-rewrite-arrow" />
                        <div>
                            <div className="wk-ask-rewrite-side wk-ask-rewrite-side--to">{ASK_COPY.inTheAnswer}</div>
                            <div className="wk-ask-rewrite-to">{rewrite.to}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
